'use client';
import '@/assets/styles/search-page.scss';
import dict from '@/dictionaries/fa.json';
import SearchPageFallbackImg from '@/public/images/search/search-page-fallback-img.svg';
import useSearch from '@/app/search/services/useSearch';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import {
    ChevronLeftSvg,
    GoBack,
    RefreshSvg,
    RoundedCloseSvg,
    SearchSvg
} from '@/assets/Icons';
import { Button } from '@/components/ui/Button';
import SourcePageTrackerProvider from '@/context/sourcePageTracker/sourcePageTrackerProvider';
import SearchCategories from '@/app/search/components/SearchCategories';
import SearchResultContestCard from '@/app/search/components/SearchResultContestCard';
import SearchResultVideoCard from '@/app/search/components/SearchResultVideoCard';
import SearchResultAudioCard from '@/app/search/components/SearchResultAudioCard';
import Filters from '@/app/search/components/Filters';
import { Input } from '@/components/ui/Input';
import useLayoutConfig from '@/context/layoutConfig/useLayoutConfig';
import { RadioFilterItem, SelectFilterItem } from '@/app/search/models/filters';
// import { TrashIcon } from '@/assets/Icons';
import useStore from '@/services/store/useStore';
import { cn } from '@/helpers/cn';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import useInfiniteLoading from '@/helpers/useInfiniteLoading';
import Image from 'next/image';
import {
    getFiltersFromSearchParams,
    getSearchParamsFromFilters
} from '@/app/search/helpers/syncFiltersWithUrlParamsMethods';

const SearchPage = () => {
    const router = useRouter();
    const pathname = usePathname();

    useLayoutConfig({ showNavbar: false, showHobe: false });


    const {
        recentSearches,
        appliedFilters,
        setAppliedFilters
    } = useStore(state => state);

    const searchParams = useSearchParams();
    const hasSearchQueryOnPageStart = searchParams.size > 0;
    const [searchPhrase, setSearchPhrase] = useState<string>((Object.fromEntries(searchParams)?.text && Object.fromEntries(searchParams)?.text !== '*') ? Object.fromEntries(searchParams)?.text : '');
    const [showRecommendations, setShowRecommendations] = useState(false);
    const [showResults, setShowResults] = useState(hasSearchQueryOnPageStart);
    const [showCategories, setShowCategories] = useState(!hasSearchQueryOnPageStart);
    const [searchRecommendationsQuery, setSearchRecommendationsQuery] = useState('');
    const [searchResultsQuery, setSearchResultsQuery] = useState(hasSearchQueryOnPageStart ? searchParams.toString() : '');
    const [showFirstLookSuggestions, setShowFirstLookSuggestions] = useState(false);

    const filtersAndRouteParamsAreSync = useRef(false);

    /* for the first time, if no url filters was set before used this flag to indicate if we have used backend set filters or not */
    const firstTimeSettingFilters = useRef(!searchParams.size);

    const {
        searchRecommendations,
        searchFilters,
        searchHotContents,
        searchResults,
        searchResultsFetchNextPage,
        searchResultsIsFetchingNextPage,
        searchResultsIsLoading
    } = useSearch({
        searchPhrase,
        showRecommendations,
        showResults,
        searchRecommendationsQuery,
        searchResultsQuery
    });

    const searchBarRef = useRef(null);

    const hotResults = useMemo(() => {
        return searchHotContents?.data?.[0];
    }, [searchHotContents]);

    const categoryFilters = useMemo(() => {
        if (!searchFilters?.data) {
            return null;
        }

        return searchFilters?.data.children.find(filter => filter.key === 'ctype');

    }, [searchFilters]);

    const shouldShowFirstLookSuggestions = useMemo(() => {
        return showFirstLookSuggestions && !searchRecommendations?.data && !searchResults?.pages;
    }, [showFirstLookSuggestions, searchRecommendations, searchResults]);

    const shouldShowRecommendations = useMemo(() => {
        return showRecommendations && searchRecommendations?.data;
    }, [showRecommendations, searchRecommendations]);

    const shouldShowCategories = useMemo(() => {
        return showCategories && categoryFilters;
    }, [showCategories, categoryFilters]);
    /* end show steps flags */

    /* Note: for now this works fine only for radio filters, for other types, be sure to check it,
     now the only filters with default mode are of type radio */
    const defaultFiltersFromApi = useMemo(() => {
        if (searchFilters) {
            return searchFilters.data.children.reduce((prev, current) => {
                const defaultItem = current.children.items.find(item => (item as RadioFilterItem)?.is_default
                    && (item as RadioFilterItem)?.value
                );
                if (defaultItem) {
                    return { ...prev, [current.key]: defaultItem?.value };
                } else {
                    return { ...prev };
                }
            }, {});
        } else {
            return {};
        }
    }, [searchFilters]);

    useEffect(() => {
        if (!searchParams.size) {
            setSearchPhrase('');
            setShowCategories(true);
            setShowFirstLookSuggestions(false);
            setShowRecommendations(false);
            setShowResults(false);
            return;
        }

        if (searchParams.toString() !== searchResultsQuery) {
            if (searchParams.toString() !== getSearchParamsFromFilters({
                filters: appliedFilters,
                phrase: searchPhrase
            })) {
                const urlSearchPhrase = searchParams.get('text');
                setSearchPhrase((!urlSearchPhrase || urlSearchPhrase === '*') ? '' : urlSearchPhrase);
                setAppliedFilters(getFiltersFromSearchParams(Object.fromEntries(searchParams), searchFilters));
            }

            setSearchResultsQuery(searchParams.toString());
        }

        setShowCategories(false);
        setShowFirstLookSuggestions(false);
        setShowRecommendations(false);
        setShowResults(true);
    }, [searchParams]);

    useEffect(() => {
        if (searchFilters) {
            setAppliedFilters(getFiltersFromSearchParams({
                ...(firstTimeSettingFilters.current ? defaultFiltersFromApi : {}),
                ...Object.fromEntries(searchParams)
            }, searchFilters));

            firstTimeSettingFilters.current = false;

            setTimeout(() => {
                /* used the timeout to have a delay, since before the filtersAndRouteParamsAreSync is true,
                 setAppliedFilter makes useEffects on appliedFilters react to this change, and for the first
                  time we don't need this reaction */
                filtersAndRouteParamsAreSync.current = true;
            }, 0);
        }
    }, [searchFilters]);

    useEffect(() => {
        const noFiltersSet = !Object.keys(appliedFilters).length;
        if ((!showResults && noFiltersSet) || !filtersAndRouteParamsAreSync.current) {
            return;
        }
        handleSearch({});
    }, [appliedFilters]);

    const handleFocus = () => {
        if (!searchResults?.pages) {
            // for the first time
            setShowFirstLookSuggestions(true);
        } else {
            // for the rest of times
            fetchRecommendationsWithDelay(searchPhrase);
            setShowRecommendations(true);
        }
        setShowResults(false);
        setShowCategories(false);
    };

    const fetchRecommendationsWithDelay = useCallback(debounce((value) => {
        setSearchRecommendationsQuery(new URLSearchParams({ text: value || '*' }).toString());
    }, 600), []);

    const handleChange = (e) => {
        setShowCategories(false);
        setShowFirstLookSuggestions(false);
        setShowResults(false);
        setShowRecommendations(true);
        setSearchPhrase(e.target.value);
        fetchRecommendationsWithDelay(e.target.value);
    };

    const handleSearch = ({ phrase, filters }: { phrase?: string, filters?: any }) => {

        if (phrase) {
            setSearchPhrase(phrase);
        }

        const decoratedSearchParams = getSearchParamsFromFilters({
            filters: filters || appliedFilters, phrase: phrase || searchPhrase
        });

        if (searchParams.toString() !== decoratedSearchParams) {
            router.push(`${pathname}?${decoratedSearchParams}`);
        } else {
            setShowCategories(false);
            setShowFirstLookSuggestions(false);
            setShowRecommendations(false);
            setShowResults(true);
        }
    };

    const handleKeyDown = (e) => {
        if (e.code === 'Enter' || e.key === 'Enter') {
            handleSearch({ phrase: e.target.value });
        }
    };

    const removeSearchPhrase = () => {
        // TODO: bring these to a better part or a reducer or something
        setShowCategories(false);
        setShowFirstLookSuggestions(false);
        setShowResults(false);
        setShowRecommendations(true);
        setSearchPhrase('');
        fetchRecommendationsWithDelay('');
        setTimeout(() => {
            searchBarRef.current?.focus();
        }, 0);
    };

    const handleCategorySelect = (selectedCategory) => {
        const newFilters = { ...appliedFilters, ctype: new Set([selectedCategory]) };
        setAppliedFilters(newFilters);
    };

    const handleRecommendationItemSelect = (title: string, multiMediaType: string) => {
        const { ctype: _, ...newFilters } = appliedFilters;

        handleSearch({ phrase: title, filters: { ctype: [multiMediaType], ...newFilters } });
    };

    useInfiniteLoading({
        scrollingElementSelector: '#search-page-container',
        loaderMethod: () => setTimeout(searchResultsFetchNextPage, 0),
        toBeWatchedData: searchResults,
        loading: searchResultsIsFetchingNextPage,
        isDeactive: !showResults
    });

    // TODO: check the values sent for the log collector
    return (
        <SourcePageTrackerProvider sourcePageData={{ sourcePage: 'search', searchQuery: searchPhrase }}>
            <main id="search-page-container"
                  className={cn('search-page-container page-container', { '!pt-[144px]': showResults })}>
                <div className="search-bar-container">
                    <Button variant="ghost" size="smallIcon" className="me-2.5" onClick={() => router.push('/')}>
                        <GoBack className="search-page-back-btn" />
                    </Button>
                    <div className="search-bar-input-container">
                        <SearchSvg className="search-bar-search-icon w-5 h-5" />
                        {!!searchPhrase.length && (
                            <Button
                                onClick={removeSearchPhrase}
                                variant="ghost"
                                size="smallIcon"
                                className="flex justify-center items-center absolute top-3.5 left-3 text-icon w-5 h-5"
                            >
                                <RoundedCloseSvg />
                            </Button>
                        )}
                        <Input
                            type="text"
                            className="pl-11 pr-[38px] h-full w-full text-neutral-900 border-neutral-300 !placeholder-neutral-700 focus-visible:!placeholder-neutral-600 focus-visible:ring-0 focus-visible:outline-primary-500 focus-visible:outline-offset-[-2px] focus-visible:outline-[2px]"
                            placeholder={dict.search.searchPlaceholder}
                            ref={searchBarRef}
                            value={searchPhrase}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            onKeyDown={handleKeyDown}
                        />
                    </div>
                </div>

                {showResults && <Filters />}

                {/*categories*/}
                {
                    shouldShowCategories &&
                    <div className="search-page-categories-container">
                        {categoryFilters?.children.items.map((categoryFilter: SelectFilterItem, index) =>
                            <SearchCategories text={categoryFilter.label}
                                              bgImg={categoryFilter.banner}
                                              key={index}
                                              onClick={() => handleCategorySelect(categoryFilter.value)}
                            />
                        )}
                    </div>
                }

                {/*first look suggestions*/}
                {
                    shouldShowFirstLookSuggestions &&
                    <div className="w-full">
                        {/*recent searches*/}
                        {
                            (recentSearches?.length > 0) &&
                            <ul>
                                <div className="font-bold text-base mb-4 flex items-center justify-between">
                                    <span>
                                        {dict.search.yourRecentSearches}
                                    </span>
                                    {/*<Button variant="ghost" size="smallIcon">*/}
                                    {/*    <TrashIcon />*/}
                                    {/*</Button>*/}
                                </div>
                                {
                                    recentSearches.slice(0, 5).map(phrase =>
                                        <li key={phrase} className="text-sm mb-4 last:mb-6 flex gap-1 items-center"
                                            onClick={() => handleSearch({ phrase })}
                                        >
                                            <RefreshSvg />
                                            {phrase}
                                        </li>
                                    )
                                }
                            </ul>
                        }
                        {/*hot results*/}
                        {
                            (hotResults && hotResults.children.length > 0) &&
                            <ul>
                                <div className="font-bold text-base mb-4">
                                <span>
                                    {hotResults.title}
                                </span>
                                </div>
                                {
                                    hotResults.children.map(item =>
                                        <li className="text-sm mb-4 last:mb-6 flex gap-1 items-center"
                                            key={item.title}
                                            onClick={() => {
                                                handleSearch({ phrase: item.title });
                                            }}
                                        >
                                            <img src={hotResults.children_icon} alt="hot-results-icon"
                                                 className="w-4 h-4" />
                                            {item.title}
                                        </li>
                                    )
                                }
                            </ul>
                        }
                    </div>
                }

                {/*related suggestions*/}
                {
                    shouldShowRecommendations &&
                    <div className="w-full">
                        {searchRecommendations?.data.map(({
                                                              key,
                                                              title,
                                                              parent_icon,
                                                              children,
                                                              children_icon,
                                                              multi_media_type
                                                          }, index) => (
                            !!children.length && <ul key={index}>
                                <div className="font-bold text-base mb-4 flex gap-2 items-center">
                                    <img src={parent_icon} alt="content-type-icon" className="w-11 h-11" />
                                    {title}
                                </div>
                                {children.slice(0, 5).map(({ title }, index) => (
                                    <li className="text-sm mb-4 last:mb-6 flex gap-1 items-center cursor-pointer"
                                        key={index}
                                        onClick={() => handleRecommendationItemSelect(title, multi_media_type)}
                                    >
                                        <img src={children_icon} alt="content-type-icon" className="w-4 h-4" />
                                        {title}
                                    </li>
                                ))}
                                {(children.length > 5) &&
                                    <li className="text-base text-[#1D3557] font-semibold mb-4 last:mb-6 flex gap-1 items-center cursor-pointer"
                                        key={index}
                                        onClick={() => handleCategorySelect(multi_media_type)}
                                    >
                                        دیدن همه نتایج
                                        <ChevronLeftSvg className="w-5 h-5" />
                                    </li>
                                }
                            </ul>
                        ))}
                    </div>
                }

                {/*search page result cards*/}
                {
                    showResults && searchResults?.pages?.map(page => page?.data?.cards?.map((card, index) => (
                            <div key={index} className="w-full">
                                {
                                    (card.data_type === 'quiz') ?
                                        <SearchResultContestCard
                                            cardData={card}
                                            className="my-4" /> :
                                        (card.data_type === 'audio') ?
                                            <SearchResultAudioCard cardData={card} className="my-4" /> :
                                            <SearchResultVideoCard
                                                isSeries={card.data_type !== 'movie'}
                                                cardData={card}
                                                className="my-4" />
                                }
                                <div className="bg-neutral-400 h-[1px] shrink-0 w-[calc(100%+32px)] mx-[-16px]"></div>
                            </div>
                        ))
                    )
                }

                {/*empty state fallback*/}
                {
                    (showResults && !searchResultsIsLoading && !searchResults?.pages?.[0]?.data?.total) &&
                    <div className="flex flex-col items-center mt-5">
                        <Image src={SearchPageFallbackImg.src} className="mb-9" alt="search-page-fallback-img"
                               width={201} height={197} />
                        <div
                            className="text-base font-semibold">{`${dict.search.fallbackTitle[0]} (${searchPhrase}) ${dict.search.fallbackTitle[1]}`}</div>
                        <div className="mt-1 text-neutral-700 text-sm">{dict.search.fallbackDesc}</div>
                    </div>
                }
            </main>
        </SourcePageTrackerProvider>
    );
};

export default SearchPage;